import {
  FunctionComponent,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
} from "react";
import { MapContext } from "../MapContext";
import { InfoWindow, useMap } from "@vis.gl/react-google-maps";
import { Text } from "@ui-kit/Text/Text";
import { colors } from "@ui-kit/ConfigProvider/ConfigProvider";
import { AddRemoveButton } from "../../../modules/components/buttons/AddRemoveButton/AddRemoveButton";
import { Image } from "@ui-kit/Image/Image";
import { useWindowSize } from "@hooks/useWindowSize/useWindowSize";

import "./MapInfoWindow.css";
import { useNavigate } from "@hooks/useNavigate/useNavigate";
import { getEntityUrlPath } from "@lib/getEntityUrlPath";

const MapInfoWindow = () => {
  const { selectedMarker, setSelectedMarker } = useContext(MapContext);
  const { isMobile } = useWindowSize();
  const navigate = useNavigate();
  const map = useMap();

  useEffect(() => {
    if (!map) {
      return;
    }

    const listener = map.addListener("click", () => {
      setSelectedMarker(null);
    });

    return () => {
      listener.remove();
    };
  }, [map, setSelectedMarker]);

  const handleClick = useCallback(() => {
    if (isMobile) {
      // Desktop navigates to entity when marker is pressed, for mobile clicking the info window.
      navigate(
        getEntityUrlPath(
          selectedMarker.slug,
          selectedMarker.entityId,
          selectedMarker.entityType,
        ),
      );
    }
  }, [isMobile, navigate, selectedMarker]);

  if (!selectedMarker) {
    return null;
  }

  return (
    <MapInfoWindowWrapper position={selectedMarker.position}>
      <div className="map-info-window-component" onClick={handleClick}>
        {selectedMarker.thumbnailUrl && (
          <div className="image">
            <Image src={selectedMarker.thumbnailUrl} />
          </div>
        )}
        <div className="body">
          <div className="details">
            <span className="name">
              <Text variant="fontHeadingXsmall" color={colors.black}>
                {selectedMarker.name}
              </Text>
            </span>
            <Text variant="fontBodyMedium" color={colors.neutral600}>
              {selectedMarker.categoryName}
            </Text>
          </div>
          <div className="add-remove-button-container">
            <AddRemoveButton
              type={selectedMarker.entityType as "Event" | "Place" | "Goal"}
              id={selectedMarker.entityId}
              variant="small"
            />
          </div>
        </div>
      </div>
    </MapInfoWindowWrapper>
  );
};

/**
 * Render as Google Map Info Window on desktop and as card at bottom screen if on mobile.
 */
const MapInfoWindowWrapper: FunctionComponent<
  PropsWithChildren & { position?: google.maps.LatLng }
> = ({ children, position }) => {
  const { isMobile } = useWindowSize();

  if (isMobile) {
    return <div className="map-info-window-component-mobile">{children}</div>;
  }

  return (
    <InfoWindow
      position={position}
      pixelOffset={[0, -60]}
      maxWidth={360}
      minWidth={360}
      headerDisabled={true}>
      {children}
    </InfoWindow>
  );
};

export { MapInfoWindow };
